import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * Customer type — individual person or organization.
 *
 * @since 1.0.0
 */
export const CustomerType = Schema.Literal("individual", "organization");
export type CustomerType = Schema.Schema.Type<typeof CustomerType>;

/**
 * Customer reseller-identity role.
 *
 * Distinct from `CustomerType` (individual|organization) and from the
 * membership role on `customer_members`. Mirrors the `people.roles`
 * convention: a `text[]` of role literals.
 * - customer: a direct customer (default)
 * - reseller: sells FlirtMachine through to downstream "sold-through" customers
 *
 * @since 1.0.0
 */
export const CustomerRole = Schema.Literal("customer", "reseller");
export type CustomerRole = Schema.Schema.Type<typeof CustomerRole>;

/**
 * Billing cadence for a customer's negotiated per-agent rate.
 *
 * @since 1.0.0
 */
export const AgentRatePlan = Schema.Literal("weekly", "monthly", "annual");
export type AgentRatePlan = Schema.Schema.Type<typeof AgentRatePlan>;

/**
 * Customer model representing a billing/contract entity.
 *
 * A customer is an ACCOUNT / billing entity, not a contact. Human identity
 * (email, phone, the human's own name) lives on `Person`; the customer points
 * at its main human via `primaryContactPersonId`. `name` is the ACCOUNT / ORG
 * name (e.g. "Acme Co"); for individuals it is defaulted from the primary
 * contact's name.
 *
 * Integrates with:
 * - Clerk: Organization identity (clerkOrgId)
 * - Stripe: Payment processing (stripeCustomerId)
 *
 * Contact identity:
 * - primaryContactPersonId: FK to the main human (`people.id`) to talk to.
 *   Sits alongside `customer_members`; the pointed-to person should also be a
 *   member. `null` until a contact is linked.
 *
 * Reseller identity & negotiated pricing:
 * - roles: reseller-identity roles (default `["customer"]`)
 * - resellerId: self-reference to the reseller that sold this customer
 *   through; `null` for direct customers
 * - resellerAttributedAt: when the current attribution was recorded; stamped
 *   whenever `resellerId` is written non-null and cleared on detach; `null`
 *   for direct customers
 * - agentRateAmount / agentRatePlan: per-agent rate (cents) negotiated once
 *   at intake and reused by self-serve agents
 *
 * @since 1.0.0
 */
export class Customer extends Model.Class<Customer>("Customer")({
  id: Model.Generated(Schema.String),
  type: CustomerType,
  name: Schema.String,
  primaryContactPersonId: Schema.NullOr(Schema.String),
  roles: Schema.Array(CustomerRole),
  resellerId: Schema.NullOr(Schema.String),
  resellerAttributedAt: Schema.NullOr(Schema.DateTimeUtc),
  agentRateAmount: Schema.NullOr(Schema.Int),
  agentRatePlan: Schema.NullOr(AgentRatePlan),
  clerkOrgId: Schema.NullOr(Schema.String),
  stripeCustomerId: Schema.NullOr(Schema.String),
  slug: Schema.NullOr(Schema.String),
  notes: Schema.NullOr(Schema.String),
  postalAddress: Schema.NullOr(Schema.String),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
